'use client';

import { mergeStandardAmbientSoundDefinitions, mysteryRoomPickStandardAmbientTrackId } from './ambientLibrary';
import { LISAD_9_MAP } from './lisad9Map';
import { LISAD_9_INFECTION_MAP } from './lisad9InfectionMap';
import { getMysteryRoomDefaultTexture, getMysteryRoomTextureTileSize } from './defaultTextures';
import {
  getMysteryRoomAccessLevelForCardLevel,
  normalizeMysteryRoomAccessCardLevel,
} from './itemAssets';
import { rectCenter } from './mapTypes';
import type {
  MysteryRoomAccessCardLevel,
  MysteryRoomAccessLevel,
  MysteryRoomAssetTransform,
  MysteryRoomCollisionShape,
  MysteryRoomCustomObjectDefinition,
  MysteryRoomCustomObjectShape,
  MysteryRoomDoorAccessMode,
  MysteryRoomDoorDefinition,
  MysteryRoomMapDefinition,
  MysteryRoomRect,
  MysteryRoomSpawnSlotDefinition,
  MysteryRoomVec2,
  MysteryRoomSurfaceTexture,
  MysteryRoomTextureLayer,
  MysteryRoomWorldLabelVisibility,
} from './mapTypes';

function normalizeOutlineWidth(value: number | null | undefined, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }
  return Math.max(0, Math.min(100, Math.round(value)));
}

const VALID_DOOR_ACCESS_MODES: MysteryRoomDoorAccessMode[] = [
  'public',
  'npc_only',
  'card_required',
  'all_cards_required',
];
const VALID_ACCESS_LEVELS: MysteryRoomAccessLevel[] = ['public', 'lab', 'server', 'reactor'];

const MAPS: Record<string, MysteryRoomMapDefinition> = {
  [LISAD_9_MAP.id]: normalizeMysteryRoomMapDefinition(LISAD_9_MAP),
  [LISAD_9_INFECTION_MAP.id]: normalizeMysteryRoomMapDefinition(LISAD_9_INFECTION_MAP),
};
const BUILTIN_MAP_IDS = new Set([LISAD_9_MAP.id, LISAD_9_INFECTION_MAP.id]);

export type MysteryRoomResolvedMapSelection = {
  id: string;
  title: string;
  definition: MysteryRoomMapDefinition;
  builtin: boolean;
};

export function getMysteryRoomMapDefinition(mapId?: string | null): MysteryRoomMapDefinition {
  if (mapId && MAPS[mapId]) {
    return MAPS[mapId];
  }
  return MAPS[LISAD_9_MAP.id];
}

export function findMysteryRoomMapDefinition(mapId?: string | null): MysteryRoomMapDefinition | null {
  if (!mapId) {
    return null;
  }
  return MAPS[mapId] ?? null;
}

export function getBuiltinMysteryRoomMapDefinition(): MysteryRoomMapDefinition {
  return MAPS[LISAD_9_MAP.id];
}

export function getBuiltinMysteryRoomMapDefinitions(): MysteryRoomMapDefinition[] {
  return [MAPS[LISAD_9_MAP.id], MAPS[LISAD_9_INFECTION_MAP.id]].filter(Boolean);
}

export function resolveMysteryRoomMapSelection(mapId?: string | null): MysteryRoomResolvedMapSelection {
  const definition = findMysteryRoomMapDefinition(mapId) ?? getBuiltinMysteryRoomMapDefinition();
  return {
    id: definition.id,
    title: definition.title,
    definition,
    builtin: BUILTIN_MAP_IDS.has(definition.id),
  };
}

export function registerMysteryRoomMapDefinition(map: MysteryRoomMapDefinition): MysteryRoomMapDefinition {
  const normalized = normalizeMysteryRoomMapDefinition(map);
  MAPS[normalized.id] = normalized;
  return normalized;
}

export function normalizeMysteryRoomMapDefinition(map: MysteryRoomMapDefinition): MysteryRoomMapDefinition {
  const afterBuiltinDefinitions = normalizeBuiltinSoundDefinitions(map);
  const afterBuiltinSources = normalizeBuiltinSoundSources(map);
  const { soundDefinitions: normalizedSoundDefinitions, soundSources: normalizedSoundSources } = normalizePrimitiveSynthAmbientLoops(
    afterBuiltinDefinitions,
    afterBuiltinSources,
  );
  const spawnSlots = normalizeMysteryRoomSpawnSlots(map.spawnSlots, map.spawn, map.spawnZoneId);
  const primarySpawn = spawnSlots[0] ?? { x: map.spawn?.x ?? 0, y: map.spawn?.y ?? 0, room: map.spawnZoneId };
  return {
    ...map,
    spawn: { x: primarySpawn.x, y: primarySpawn.y },
    spawnZoneId: primarySpawn.room ?? primarySpawn.zoneId ?? map.spawnZoneId,
    spawnSlots,
    rooms: (map.rooms ?? []).map((room) => ({
      ...room,
      colorEnabled: room.colorEnabled ?? true,
      colorOpacity: normalizeSurfaceOpacity(room.colorOpacity ?? 0.9),
      outlineColor: room.outlineColor ?? 0xf8fafc,
      outlineEnabled: room.outlineEnabled ?? true,
      outlineOpacity: normalizeSurfaceOpacity(room.outlineOpacity ?? 0.08),
      outlineWidth: normalizeOutlineWidth(room.outlineWidth, 4),
      floorTexture: normalizeMysteryRoomSurfaceTexture(room.floorTexture),
      textures: normalizeMysteryRoomTextureLayers(room.textures, room.floorTexture, 'floor'),
    })),
    walls: (map.walls ?? []).map((wall) => ({
      ...wall,
      colorEnabled: wall.colorEnabled ?? true,
      colorOpacity: normalizeSurfaceOpacity(wall.colorOpacity ?? 0.72),
      outlineColor: wall.outlineColor ?? 0xe2e8f0,
      outlineEnabled: wall.outlineEnabled ?? true,
      outlineOpacity: normalizeSurfaceOpacity(wall.outlineOpacity ?? 0.52),
      outlineWidth: normalizeOutlineWidth(wall.outlineWidth, 1),
      texture: normalizeMysteryRoomSurfaceTexture(wall.texture),
      textures: normalizeMysteryRoomTextureLayers(wall.textures, wall.texture, 'wall'),
    })),
    doors: (map.doors ?? []).map((door) => normalizeMysteryRoomDoorDefinition(door)),
    occluders: map.occluders ?? [],
    interactables: (map.interactables ?? []).map((entry) => ({
      ...entry,
      worldLabelVisibility: normalizeWorldLabelVisibility(entry.worldLabelVisibility),
      colorEnabled: entry.colorEnabled ?? true,
      colorOpacity: normalizeSurfaceOpacity(entry.colorOpacity ?? 0.96),
      outlineColor: entry.outlineColor ?? 0xf8fafc,
      outlineEnabled: entry.outlineEnabled ?? true,
      outlineOpacity: normalizeSurfaceOpacity(entry.outlineOpacity ?? 0.7),
      outlineWidth: normalizeOutlineWidth(entry.outlineWidth, 4),
      textures: normalizeMysteryRoomTextureLayers(entry.textures),
      inventoryIcon: normalizeInventoryIconText(entry.inventoryIcon),
      inventoryIconAssetId: normalizeOptionalString(entry.inventoryIconAssetId),
      inventoryIconAssetUrl: normalizeOptionalString(entry.inventoryIconAssetUrl),
      inventoryIconAssetName: normalizeOptionalString(entry.inventoryIconAssetName),
      inventoryIconAssetMimeType: normalizeOptionalString(entry.inventoryIconAssetMimeType),
      defaultEnabled: entry.kind === 'light_switch' ? entry.defaultEnabled ?? true : entry.defaultEnabled,
    })),
    workAnchors: (map.workAnchors ?? []).map(normalizeMysteryRoomWorkAnchor),
    navNodes: map.navNodes ?? [],
    navEdges: map.navEdges ?? [],
    incidentAnchors: map.incidentAnchors ?? [],
    patrolRoutes: map.patrolRoutes ?? [],
    npcs: (map.npcs ?? []).map(normalizeMysteryRoomNpcDefinition),
    lights: (map.lights ?? []).map((light) => {
      const flickerMinIntervalMs = normalizeLightInterval(light.flickerMinIntervalMs, 90);
      const flickerMaxIntervalMs = Math.max(
        flickerMinIntervalMs,
        normalizeLightInterval(light.flickerMaxIntervalMs, Math.max(180, flickerMinIntervalMs)),
      );
      return {
        ...light,
        colorEnabled: light.colorEnabled ?? true,
        colorOpacity: normalizeSurfaceOpacity(light.colorOpacity ?? 0.96),
        outlineColor: light.outlineColor ?? 0xfff7ed,
        outlineEnabled: light.outlineEnabled ?? true,
        outlineOpacity: normalizeSurfaceOpacity(light.outlineOpacity ?? 0.72),
        outlineWidth: normalizeOutlineWidth(light.outlineWidth, 4),
        colorTemperatureK: normalizeLightTemperature(light.colorTemperatureK),
        flickerMode: normalizeLightFlickerMode(light.flickerMode, light.flicker),
        flickerIntensity: normalizeSurfaceOpacity(light.flickerIntensity ?? (light.flicker ? 0.32 : 0.26)),
        flickerMinIntervalMs,
        flickerMaxIntervalMs,
        textures: normalizeMysteryRoomTextureLayers(light.textures),
      };
    }),
    hazards: (map.hazards ?? []).map((hazard) => ({ ...hazard, textures: normalizeMysteryRoomTextureLayers(hazard.textures) })),
    props: (map.props ?? []).map((prop) => ({
      ...prop,
      colorEnabled: prop.colorEnabled ?? true,
      colorOpacity: normalizeSurfaceOpacity(prop.colorOpacity ?? 0.94),
      outlineColor: prop.outlineColor ?? prop.accentColor ?? 0x38bdf8,
      outlineEnabled: prop.outlineEnabled ?? true,
      outlineOpacity: normalizeSurfaceOpacity(prop.outlineOpacity ?? 0.46),
      outlineWidth: normalizeOutlineWidth(prop.outlineWidth, 3),
      textures: normalizeMysteryRoomTextureLayers(prop.textures),
    })),
    customObjects: (map.customObjects ?? []).map(normalizeMysteryRoomCustomObject),
    triggers: (map.triggers ?? []).map((trigger) => ({
      ...trigger,
      colorEnabled: trigger.colorEnabled ?? true,
      colorOpacity: normalizeSurfaceOpacity(trigger.colorOpacity ?? 0.12),
      outlineColor: trigger.outlineColor ?? 0xc4b5fd,
      outlineEnabled: trigger.outlineEnabled ?? true,
      outlineOpacity: normalizeSurfaceOpacity(trigger.outlineOpacity ?? 0.72),
      outlineWidth: normalizeOutlineWidth(trigger.outlineWidth, 5),
      textures: normalizeMysteryRoomTextureLayers(trigger.textures),
    })),
    soundDefinitions: normalizedSoundDefinitions,
    soundSources: (normalizedSoundSources ?? []).map((source) => ({
      ...source,
      uniformMapVolume: Boolean(source.uniformMapVolume),
      uniformVolumeInRadius: Boolean(source.uniformVolumeInRadius),
    })),
  };
}

/** Зацикленные источники с «примитивным» fan/ambient без URL переводят на один из шести MP3. */
function normalizePrimitiveSynthAmbientLoops(
  definitions: MysteryRoomMapDefinition['soundDefinitions'],
  sources: MysteryRoomMapDefinition['soundSources'],
): Pick<MysteryRoomMapDefinition, 'soundDefinitions' | 'soundSources'> {
  const mergedDefs = mergeStandardAmbientSoundDefinitions(definitions ?? []);
  const byId = new Map(mergedDefs.map((definition) => [definition.id, definition]));
  const nextSources = (sources ?? []).map((source) => {
    if (!source.loop) {
      return source;
    }
    const def = byId.get(source.soundId);
    if (!def) {
      return source;
    }
    const hasUrl = typeof def.url === 'string' && def.url.length > 0;
    if (hasUrl) {
      return source;
    }
    if (def.kind !== 'fan' && def.kind !== 'ambient') {
      return source;
    }
    return { ...source, soundId: mysteryRoomPickStandardAmbientTrackId(source.id) };
  });
  return { soundDefinitions: mergedDefs, soundSources: nextSources };
}

function normalizeBuiltinSoundDefinitions(map: MysteryRoomMapDefinition): MysteryRoomMapDefinition['soundDefinitions'] {
  const current = map.soundDefinitions ?? [];
  if (map.id !== LISAD_9_MAP.id) {
    return current;
  }
  const hasNewAmbient = current.some((entry) => entry.id === 'ambient_track_1' && typeof entry.url === 'string' && entry.url.length > 0);
  if (hasNewAmbient) {
    return current;
  }
  return LISAD_9_MAP.soundDefinitions.map((entry) => ({ ...entry }));
}

function normalizeBuiltinSoundSources(map: MysteryRoomMapDefinition): MysteryRoomMapDefinition['soundSources'] {
  const current = map.soundSources ?? [];
  if (map.id !== LISAD_9_MAP.id) {
    return current;
  }
  const hasNewAmbientSources = current.some((entry) => entry.id === 'ambient_cross' && entry.soundId === 'ambient_track_1');
  if (hasNewAmbientSources) {
    return current;
  }
  return LISAD_9_MAP.soundSources.map((entry) => ({ ...entry, position: { ...entry.position } }));
}

function normalizeMysteryRoomWorkAnchor(anchor: MysteryRoomMapDefinition['workAnchors'][number]): MysteryRoomMapDefinition['workAnchors'][number] {
  return {
    ...anchor,
    taskAreaShape: anchor.taskAreaShape === 'square' ? 'square' : 'circle',
    taskAreaRadius: Math.max(8, normalizePositiveNumber(anchor.taskAreaRadius, defaultWorkAnchorAreaRadius(anchor.kind))),
    description: normalizeOptionalString(anchor.description) ?? defaultWorkAnchorDescription(anchor.kind),
  };
}

function defaultWorkAnchorAreaRadius(kind: MysteryRoomMapDefinition['workAnchors'][number]['kind']) {
  switch (kind) {
    case 'restroom':
      return 28;
    case 'break_room':
    case 'standby':
      return 96;
    case 'patrol_point':
    case 'checkpoint':
      return 72;
    case 'evacuation':
      return 110;
    default:
      return 84;
  }
}

function defaultWorkAnchorDescription(kind: MysteryRoomMapDefinition['workAnchors'][number]['kind']) {
  switch (kind) {
    case 'restroom':
      return 'Бот редко приходит сюда и ненадолго задерживается.';
    case 'break_room':
      return 'Бот отдыхает рядом с точкой, меняя короткие позиции вокруг нее.';
    case 'armory':
      return 'Охрана или оператор проверяет вооружение и готовится к тревоге.';
    case 'patrol_point':
    case 'checkpoint':
      return 'Охранник использует точку как часть патруля или пост наблюдения.';
    case 'medical_station':
    case 'medic_work':
      return 'Медик дежурит, проверяет оборудование и реагирует на раненых.';
    case 'maintenance':
    case 'engineer_work':
      return 'Инженер обслуживает оборудование и идет сюда при поломках.';
    case 'guard_work':
      return 'Рабочее место охраны: наблюдение, доклады и проверка тревог.';
    case 'operator_work':
      return 'Оператор контролирует терминалы и рабочие процессы объекта.';
    case 'evacuation':
      return 'Боты бегут сюда при тревоге, если нет срочной задачи.';
    case 'breakdown':
      return 'Место, где может возникнуть поломка для ремонта инженером.';
    case 'experiment':
      return 'Научный сотрудник проводит эксперимент и периодически отходит рядом.';
    case 'standby':
      return 'Резервная точка ожидания для свободного бота.';
    case 'workstation':
    default:
      return 'Бот выполняет рабочую рутину и перемещается рядом с точкой.';
  }
}

function normalizeMysteryRoomSpawnSlots(
  slots: MysteryRoomSpawnSlotDefinition[] | null | undefined,
  fallbackSpawn: MysteryRoomVec2 | null | undefined,
  fallbackZoneId: string | null | undefined,
): MysteryRoomSpawnSlotDefinition[] {
  const rawSlots = Array.isArray(slots) && slots.length > 0
    ? slots
    : [{ x: fallbackSpawn?.x ?? 0, y: fallbackSpawn?.y ?? 0, room: fallbackZoneId ?? '' }];
  const sourceSlots = fallbackSpawn
    ? [{ ...rawSlots[0], x: fallbackSpawn.x, y: fallbackSpawn.y, room: rawSlots[0]?.room ?? rawSlots[0]?.zoneId ?? fallbackZoneId }, ...rawSlots.slice(1)]
    : rawSlots;
  return sourceSlots.map((slot, index) => {
    const room = normalizeOptionalString(slot.room ?? slot.zoneId ?? fallbackZoneId) ?? '';
    const actor = slot.actor === 'bot' ? 'bot' : 'player';
    const side = slot.side === 'B' ? 'B' : 'A';
    return {
      x: Number.isFinite(slot.x) ? Math.round(slot.x) : Math.round(fallbackSpawn?.x ?? 0),
      y: Number.isFinite(slot.y) ? Math.round(slot.y) : Math.round(fallbackSpawn?.y ?? 0),
      room,
      zoneId: room,
      label: normalizeOptionalString(slot.label) ?? `Игрок ${index + 1}`,
      actor,
      side,
    };
  });
}

function normalizeMysteryRoomNpcDefinition(npc: MysteryRoomMapDefinition['npcs'][number]): MysteryRoomMapDefinition['npcs'][number] {
  const maxHealth = normalizePositiveNumber(npc.maxHealth ?? npc.health, npc.actorKind === 'boss' ? 500 : 100);
  const dutyActivation = normalizeNpcDutyActivation(npc.dutyActivation);
  return {
    ...npc,
    actorKind: npc.actorKind === 'boss' ? 'boss' : 'bot',
    actorPreset: npc.actorPreset ?? (npc.actorKind === 'boss' ? 'boss' : 'npc'),
    radius: normalizePositiveNumber(npc.radius, npc.actorKind === 'boss' ? 34 : 16),
    health: normalizePositiveNumber(npc.health, maxHealth),
    maxHealth,
    speed: normalizePositiveNumber(npc.speed, npc.actorKind === 'boss' ? 88 : 118),
    visionRange: normalizePositiveNumber(npc.visionRange, npc.actorKind === 'boss' ? 720 : 560),
    visionHalfAngleDeg: normalizePositiveNumber(npc.visionHalfAngleDeg, npc.actorKind === 'boss' ? 62 : 42),
    scale: normalizePositiveNumber(npc.scale, npc.actorKind === 'boss' ? 1.9 : 1),
    invisible: npc.invisible ?? false,
    dutyActivation,
    dutyTriggerIds: normalizeStringIdList(npc.dutyTriggerIds),
    dutyDoorIds: normalizeStringIdList(npc.dutyDoorIds),
  };
}

function normalizeNpcDutyActivation(value: MysteryRoomMapDefinition['npcs'][number]['dutyActivation']) {
  return value === 'manual' || value === 'trigger' || value === 'door_open' || value === 'world_alarm'
    ? value
    : 'auto';
}

function normalizePositiveNumber(value: number | null | undefined, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }
  return Math.max(0, Math.round(value * 10) / 10);
}

function normalizeMysteryRoomDoorDefinition(door: MysteryRoomDoorDefinition): MysteryRoomDoorDefinition {
  const accessMode = normalizeDoorAccessMode(door.accessMode, door.requiredAccess);
  return {
    ...door,
    label: door.label ?? door.id,
    accessMode,
    accessCardObjectIds: normalizeStringIdList(door.accessCardObjectIds),
    colorEnabled: door.colorEnabled ?? true,
    colorOpacity: normalizeSurfaceOpacity(door.colorOpacity ?? 0.96),
    outlineColor: door.outlineColor ?? 0xf8fafc,
    outlineEnabled: door.outlineEnabled ?? true,
    outlineOpacity: normalizeSurfaceOpacity(door.outlineOpacity ?? 0.62),
    outlineWidth: normalizeOutlineWidth(door.outlineWidth, 4),
    textures: normalizeMysteryRoomTextureLayers(door.textures),
  };
}

function normalizeDoorAccessMode(
  value: MysteryRoomDoorAccessMode | string | null | undefined,
  requiredAccess: string | null | undefined,
): MysteryRoomDoorAccessMode {
  if (VALID_DOOR_ACCESS_MODES.includes(value as MysteryRoomDoorAccessMode)) {
    return value as MysteryRoomDoorAccessMode;
  }
  return !requiredAccess || requiredAccess === 'public' ? 'public' : 'card_required';
}

function normalizeStringIdList(value: string[] | null | undefined): string[] {
  return Array.from(
    new Set((Array.isArray(value) ? value : []).map((entry) => String(entry).trim()).filter(Boolean)),
  );
}

function normalizeOptionalString(value: string | null | undefined): string | undefined {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return normalized || undefined;
}

function normalizeInventoryIconText(value: string | null | undefined): string | undefined {
  const normalized = typeof value === 'string' ? value.trim().slice(0, 4).toUpperCase() : '';
  return normalized || undefined;
}

function normalizeWorldLabelVisibility(value: string | null | undefined): MysteryRoomWorldLabelVisibility {
  return value === 'near_interactive' || value === 'always' ? value : 'hidden';
}

function normalizeMysteryRoomCustomObject(object: MysteryRoomCustomObjectDefinition): MysteryRoomCustomObjectDefinition {
  const collision: MysteryRoomCollisionShape = object.collision ?? { kind: 'none' };
  const collisionBlocksMovement =
    collision.kind === 'rect' || collision.kind === 'circle' || collision.kind === 'polygon' ? collision.blocksMovement : false;
  const collisionEnabled = object.collisionEnabled ?? collisionBlocksMovement;
  const weightKg = typeof object.weightKg === 'number' && Number.isFinite(object.weightKg) ? object.weightKg : 25;
  const rotation = typeof object.rotation === 'number' && Number.isFinite(object.rotation) ? object.rotation : 0;
  const shape = normalizeMysteryRoomCustomObjectShape(object.shape);
  const accessCardLevel = normalizeCustomObjectAccessCardLevel(object);
  const normalizedObject = {
    ...object,
    rotation,
    shape,
    blocksSight: object.blocksSight ?? false,
  };

  return {
    ...object,
    rotation,
    worldLabelVisibility: normalizeWorldLabelVisibility(object.worldLabelVisibility),
    colorEnabled: object.colorEnabled ?? true,
    colorOpacity: normalizeSurfaceOpacity(object.colorOpacity ?? 0.9),
    outlineColor: object.outlineColor ?? object.accentColor ?? (object.blocksSight ? 0xfb7185 : 0xf8fafc),
    outlineEnabled: object.outlineEnabled ?? true,
    outlineOpacity: normalizeSurfaceOpacity(object.outlineOpacity ?? (object.interactive ? 0.88 : 0.5)),
    outlineWidth: normalizeOutlineWidth(object.outlineWidth, (object.collisionEnabled ?? collisionEnabled) ? 4 : 2),
    shape,
    assetFit: object.assetFit ?? (object.assetUrl ? 'cover' : undefined),
    assetTransform: object.assetUrl ? normalizeMysteryRoomAssetTransform(object.assetTransform) : object.assetTransform,
    assetAlphaCollisionEnabled: object.assetUrl ? object.assetAlphaCollisionEnabled ?? false : false,
    textures: normalizeMysteryRoomTextureLayers(
      object.textures,
      object.assetUrl
        ? {
            assetId: object.assetId,
            assetUrl: object.assetUrl,
            assetName: object.assetName,
            assetMimeType: object.assetMimeType,
            fit: object.assetFit ?? 'cover',
            opacity: 0.98,
            offsetX: object.assetTransform?.offsetX,
            offsetY: object.assetTransform?.offsetY,
            scaleX: object.assetTransform?.scaleX,
            scaleY: object.assetTransform?.scaleY,
            rotation: object.assetTransform?.rotation,
          }
        : undefined,
      'object',
    ),
    collision: normalizeMysteryRoomCustomObjectCollision(normalizedObject, collision, collisionEnabled),
    collisionEnabled,
    physicsEnabled: object.physicsEnabled ?? false,
    weightKg: Math.max(0, Math.round(weightKg * 10) / 10),
    accessCardLevel,
    grantsAccess: normalizeCustomObjectGrantsAccess(object, accessCardLevel),
    inventoryIcon: normalizeInventoryIconText(object.inventoryIcon),
    inventoryIconAssetId: normalizeOptionalString(object.inventoryIconAssetId),
    inventoryIconAssetUrl: normalizeOptionalString(object.inventoryIconAssetUrl),
    inventoryIconAssetName: normalizeOptionalString(object.inventoryIconAssetName),
    inventoryIconAssetMimeType: normalizeOptionalString(object.inventoryIconAssetMimeType),
    blocksSight: object.blocksSight ?? false,
    visibleThroughWalls: object.visibleThroughWalls ?? false,
    interactive: object.interactive ?? false,
    tags: object.tags ?? [],
  };
}

function normalizeCustomObjectAccessCardLevel(
  object: Pick<MysteryRoomCustomObjectDefinition, 'kind' | 'pickupKind' | 'tags' | 'accessCardLevel'>,
): MysteryRoomAccessCardLevel | undefined {
  const isAccessCard = object.kind === 'badge' || object.pickupKind === 'access_card' || object.tags?.includes('access_card');
  return isAccessCard ? normalizeMysteryRoomAccessCardLevel(object.accessCardLevel) : undefined;
}

function normalizeCustomObjectGrantsAccess(
  object: Pick<MysteryRoomCustomObjectDefinition, 'grantsAccess'>,
  accessCardLevel: MysteryRoomAccessCardLevel | undefined,
): MysteryRoomAccessLevel | undefined {
  if (accessCardLevel) {
    return getMysteryRoomAccessLevelForCardLevel(accessCardLevel);
  }
  return VALID_ACCESS_LEVELS.includes(object.grantsAccess as MysteryRoomAccessLevel)
    ? object.grantsAccess as MysteryRoomAccessLevel
    : undefined;
}

function normalizeMysteryRoomCustomObjectCollision(
  object: Pick<MysteryRoomCustomObjectDefinition, 'rect' | 'rotation' | 'shape' | 'blocksSight'>,
  collision: MysteryRoomCollisionShape,
  collisionEnabled: boolean,
): MysteryRoomCollisionShape {
  if (!collisionEnabled) {
    return { kind: 'none' };
  }
  const blocksMovement = collision.kind === 'none' ? true : collision.blocksMovement;
  const blocksSight = collision.kind === 'none' ? object.blocksSight : collision.blocksSight;
  if (object.shape?.kind === 'polygon') {
    const points = customObjectShapeWorldPoints(object.rect, object.rotation ?? 0, object.shape);
    if (points.length === 1) {
      return {
        kind: 'circle',
        center: points[0],
        radius: Math.max(8, Math.round(Math.min(object.rect.width, object.rect.height) * 0.16)),
        blocksMovement,
        blocksSight,
      };
    }
    if (points.length === 2) {
      return {
        kind: 'rect',
        rect: boundsFromPoints(points, Math.max(8, Math.round(Math.min(object.rect.width, object.rect.height) * 0.09))),
        blocksMovement,
        blocksSight,
      };
    }
    if (points.length >= 3) {
      return { kind: 'polygon', points, blocksMovement, blocksSight };
    }
  }
  if (collision.kind === 'circle') {
    return {
      ...collision,
      center: normalizeVec2(collision.center) ?? rectCenter(object.rect),
      radius: Math.max(1, Number.isFinite(collision.radius) ? Math.round(collision.radius) : Math.round(Math.min(object.rect.width, object.rect.height) / 2)),
      blocksMovement,
      blocksSight,
    };
  }
  if (collision.kind === 'polygon') {
    const points = collision.points.map(normalizeWorldPoint).filter((point): point is MysteryRoomVec2 => Boolean(point));
    return points.length >= 3 ? { kind: 'polygon', points, blocksMovement, blocksSight } : { kind: 'rect', rect: { ...object.rect }, blocksMovement, blocksSight };
  }
  return { kind: 'rect', rect: collision.kind === 'rect' ? normalizeRect(collision.rect, object.rect) : { ...object.rect }, blocksMovement, blocksSight };
}

function customObjectShapeWorldPoints(
  rect: MysteryRoomRect,
  rotation: number,
  shape: Extract<MysteryRoomCustomObjectShape, { kind: 'polygon' }>,
): MysteryRoomVec2[] {
  const center = rectCenter(rect);
  const radians = (rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return shape.points.map((point) => {
    const localX = point.x * rect.width;
    const localY = point.y * rect.height;
    return {
      x: center.x + localX * cos - localY * sin,
      y: center.y + localX * sin + localY * cos,
    };
  });
}

function boundsFromPoints(points: MysteryRoomVec2[], padding = 0): MysteryRoomRect {
  const minX = Math.min(...points.map((point) => point.x)) - padding;
  const maxX = Math.max(...points.map((point) => point.x)) + padding;
  const minY = Math.min(...points.map((point) => point.y)) - padding;
  const maxY = Math.max(...points.map((point) => point.y)) + padding;
  return {
    x: Math.round(minX),
    y: Math.round(minY),
    width: Math.max(1, Math.round(maxX - minX)),
    height: Math.max(1, Math.round(maxY - minY)),
  };
}

function normalizeRect(rect: MysteryRoomRect | undefined, fallback: MysteryRoomRect): MysteryRoomRect {
  if (!rect) {
    return { ...fallback };
  }
  return {
    x: Number.isFinite(rect.x) ? Math.round(rect.x) : fallback.x,
    y: Number.isFinite(rect.y) ? Math.round(rect.y) : fallback.y,
    width: Number.isFinite(rect.width) ? Math.max(1, Math.round(rect.width)) : fallback.width,
    height: Number.isFinite(rect.height) ? Math.max(1, Math.round(rect.height)) : fallback.height,
  };
}

function normalizeVec2(point: MysteryRoomVec2 | undefined): MysteryRoomVec2 | null {
  if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
    return null;
  }
  return { x: Math.round(point.x), y: Math.round(point.y) };
}

function normalizeWorldPoint(point: MysteryRoomVec2 | undefined): MysteryRoomVec2 | null {
  return normalizeVec2(point);
}

function normalizeMysteryRoomCustomObjectShape(
  shape: MysteryRoomCustomObjectShape | undefined,
): MysteryRoomCustomObjectShape | undefined {
  if (!shape) {
    return undefined;
  }
  if (shape.kind === 'rect') {
    return shape;
  }
  const points = (shape.points ?? [])
    .map(normalizeShapePoint)
    .filter((point): point is MysteryRoomVec2 => Boolean(point));
  if (points.length === 0) {
    return undefined;
  }
  return {
    kind: 'polygon',
    points: points.slice(0, 32),
  };
}

function normalizeShapePoint(point: MysteryRoomVec2 | null | undefined): MysteryRoomVec2 | null {
  if (
    !point ||
    typeof point.x !== 'number' ||
    typeof point.y !== 'number' ||
    !Number.isFinite(point.x) ||
    !Number.isFinite(point.y)
  ) {
    return null;
  }
  return {
    x: Math.max(-2, Math.min(2, Math.round(point.x * 1000) / 1000)),
    y: Math.max(-2, Math.min(2, Math.round(point.y * 1000) / 1000)),
  };
}

function normalizeMysteryRoomTextureLayers(
  textures?: MysteryRoomTextureLayer[],
  legacyTexture?: MysteryRoomSurfaceTexture,
  legacyPrefix = 'texture',
): MysteryRoomTextureLayer[] | undefined {
  const normalized = (textures ?? [])
    .map((texture, index) => normalizeMysteryRoomTextureLayer(texture, index))
    .filter((texture): texture is MysteryRoomTextureLayer => Boolean(texture));
  if (normalized.length > 0) {
    return normalized;
  }
  const legacy = normalizeMysteryRoomSurfaceTexture(legacyTexture);
  if (!legacy) {
    return undefined;
  }
  return [normalizeMysteryRoomTextureLayer({ ...legacy, id: `${legacyPrefix}_texture_1`, label: legacy.assetName ?? 'Текстура' }, 0)].filter(
    (texture): texture is MysteryRoomTextureLayer => Boolean(texture),
  );
}

function normalizeMysteryRoomTextureLayer(texture: MysteryRoomTextureLayer | undefined, index: number): MysteryRoomTextureLayer | null {
  if (!texture?.assetId && !texture?.assetUrl && !texture?.builtinTextureId) {
    return null;
  }
  const builtin = getMysteryRoomDefaultTexture(texture.builtinTextureId);
  if (texture.builtinTextureId && !builtin && !texture.assetId && !texture.assetUrl) {
    return null;
  }
  return {
    ...texture,
    assetName: texture.assetName ?? builtin?.label,
    assetMimeType: texture.assetMimeType ?? builtin?.assetMimeType,
    id: texture.id || `texture_${index + 1}`,
    label: texture.label ?? texture.assetName ?? builtin?.label ?? `Текстура ${index + 1}`,
    fit: texture.fit ?? 'repeat',
    opacity: normalizeSurfaceOpacity(texture.opacity),
    offsetX: normalizeFiniteAssetNumber(texture.offsetX, 0),
    offsetY: normalizeFiniteAssetNumber(texture.offsetY, 0),
    scaleX: normalizePositiveAssetScale(texture.scaleX),
    scaleY: normalizePositiveAssetScale(texture.scaleY),
    rotation: normalizeFiniteAssetNumber(texture.rotation, 0),
    repeatX: normalizePositiveTextureRepeat(texture.repeatX),
    repeatY: normalizePositiveTextureRepeat(texture.repeatY),
    tileSize: getMysteryRoomTextureTileSize(texture),
    visible: texture.visible ?? true,
    effects: {
      brightness: normalizeEffectIntensity(texture.effects?.brightness),
      contrast: normalizeEffectIntensity(texture.effects?.contrast),
      reflection: normalizeEffectIntensity(texture.effects?.reflection),
      shading: normalizeEffectIntensity(texture.effects?.shading),
      vignette: normalizeEffectIntensity(texture.effects?.vignette),
      grain: normalizeEffectIntensity(texture.effects?.grain),
    },
  };
}

function normalizeMysteryRoomSurfaceTexture(texture?: MysteryRoomSurfaceTexture): MysteryRoomSurfaceTexture | undefined {
  if (!texture?.assetId && !texture?.assetUrl && !texture?.builtinTextureId) {
    return undefined;
  }
  const builtin = getMysteryRoomDefaultTexture(texture.builtinTextureId);
  if (texture.builtinTextureId && !builtin && !texture.assetId && !texture.assetUrl) {
    return undefined;
  }
  return {
    ...texture,
    assetName: texture.assetName ?? builtin?.label,
    assetMimeType: texture.assetMimeType ?? builtin?.assetMimeType,
    fit: texture.fit ?? 'cover',
    opacity: normalizeSurfaceOpacity(texture.opacity),
    offsetX: normalizeFiniteAssetNumber(texture.offsetX, 0),
    offsetY: normalizeFiniteAssetNumber(texture.offsetY, 0),
    scaleX: normalizePositiveAssetScale(texture.scaleX),
    scaleY: normalizePositiveAssetScale(texture.scaleY),
    rotation: normalizeFiniteAssetNumber(texture.rotation, 0),
    repeatX: normalizePositiveTextureRepeat(texture.repeatX),
    repeatY: normalizePositiveTextureRepeat(texture.repeatY),
    tileSize: getMysteryRoomTextureTileSize(texture),
    effects: texture.effects
      ? {
          brightness: normalizeEffectIntensity(texture.effects.brightness),
          contrast: normalizeEffectIntensity(texture.effects.contrast),
          reflection: normalizeEffectIntensity(texture.effects.reflection),
          shading: normalizeEffectIntensity(texture.effects.shading),
          vignette: normalizeEffectIntensity(texture.effects.vignette),
          grain: normalizeEffectIntensity(texture.effects.grain),
        }
      : undefined,
  };
}

function normalizeMysteryRoomAssetTransform(transform?: MysteryRoomAssetTransform): MysteryRoomAssetTransform {
  return {
    offsetX: normalizeFiniteAssetNumber(transform?.offsetX, 0),
    offsetY: normalizeFiniteAssetNumber(transform?.offsetY, 0),
    scaleX: normalizePositiveAssetScale(transform?.scaleX),
    scaleY: normalizePositiveAssetScale(transform?.scaleY),
    rotation: normalizeFiniteAssetNumber(transform?.rotation, 0),
    clipToBounds: transform?.clipToBounds ?? true,
  };
}

function normalizeFiniteAssetNumber(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.round(value * 100) / 100 : fallback;
}

function normalizePositiveAssetScale(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 1;
  }
  return Math.max(0.05, Math.min(12, Math.round(value * 1000) / 1000));
}

function normalizePositiveTextureRepeat(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 1;
  }
  return Math.max(0.05, Math.min(20, Math.round(value * 1000) / 1000));
}

function normalizeEffectIntensity(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0;
  }
  return Math.max(-100, Math.min(100, Math.round(value)));
}

function normalizeSurfaceOpacity(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0.92;
  }
  return Math.max(0, Math.min(1, Math.round(value * 100) / 100));
}

function normalizeLightTemperature(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 4200;
  }
  return Math.max(1800, Math.min(8000, Math.round(value)));
}

function normalizeLightInterval(value: number | null | undefined, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }
  return Math.max(40, Math.min(5000, Math.round(value)));
}

function normalizeLightFlickerMode(
  value: string | null | undefined,
  legacyFlicker: boolean | undefined,
): NonNullable<MysteryRoomMapDefinition['lights'][number]['flickerMode']> {
  if (value === 'flicker' || value === 'event_flicker' || value === 'random_bulb') {
    return value;
  }
  return legacyFlicker ? 'flicker' : 'steady';
}
